import { createFileRoute, Link, redirect, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { supabase } from "@/integrations/supabase/client";
import { ConfirmDialog } from "@/components/admin/confirm-dialog";
import { deleteClient, getAdminBootstrap, getClientDetail } from "@/lib/admin.functions";

export const Route = createFileRoute("/admin/clients/$profileId")({
  ssr: false,
  beforeLoad: async ({ location }) => {
    const { data: userRes } = await supabase.auth.getUser();
    if (!userRes.user) throw redirect({ to: "/dashboard", search: { redirect: location.href } });
    try {
      const res = await getAdminBootstrap();
      if (!res.isAdmin) throw redirect({ to: "/dashboard" });
    } catch (err) {
      if (err && typeof err === "object" && "isRedirect" in err) throw err;
      throw redirect({ to: "/dashboard" });
    }
  },
  component: ClientDetailPage,
  head: () => ({
    meta: [
      { title: "Client · Admin · the Vibing Company®" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
});

function money(cents: number | null | undefined, currency: string | null | undefined) {
  if (cents == null) return "—";
  const cur = (currency ?? "usd").toUpperCase();
  return `${(cents / 100).toLocaleString("en-US", { minimumFractionDigits: 2 })} ${cur}`;
}

function date(iso: string | null | undefined) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function ClientDetailPage() {
  const { profileId } = Route.useParams();
  const navigate = useNavigate();
  const fetchDetail = useServerFn(getClientDetail);
  const removeClient = useServerFn(deleteClient);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ["admin-client", profileId],
    queryFn: async () => {
      const res = await fetchDetail({ data: { profileId } });
      if ("error" in res) throw new Error(res.error);
      return res;
    },
  });

  const deleteMut = useMutation({
    mutationFn: async () => {
      const res = await removeClient({
        data: { profileId, confirmEmail: data?.profile.email ?? "" },
      });
      if ("error" in res) throw new Error(res.error);
      return res;
    },
    onSuccess: () => {
      setConfirmOpen(false);
      navigate({ to: "/admin" });
    },
  });

  return (
    <main id="main" className="mx-auto w-full max-w-4xl px-5 py-12 sm:px-8">
      <Link
        to="/admin"
        className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground underline underline-offset-4 hover:text-foreground"
      >
        ← All clients
      </Link>

      {isLoading ? (
        <p className="mt-10 text-sm text-muted-foreground">Loading client…</p>
      ) : error ? (
        <p role="alert" className="mt-10 text-sm text-destructive">
          {(error as Error).message}
        </p>
      ) : data ? (
        <>
          <header className="mt-6 flex flex-wrap items-end justify-between gap-4">
            <div>
              <h1 className="font-display text-4xl tracking-tight text-balance">
                {data.profile.name ?? data.profile.email}
              </h1>
              <p className="mt-1 text-sm text-foreground/60">{data.profile.email}</p>
              <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                {data.status?.status ?? data.profile.status} · joined {date(data.profile.created_at)}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Link
                to="/admin/preview/$profileId"
                params={{ profileId }}
                className="rounded-xl border border-foreground/15 px-4 py-2 text-xs font-medium [transition-property:transform,opacity] duration-200 active:scale-[0.96]"
              >
                Preview dashboard
              </Link>
              <button
                type="button"
                onClick={() => setConfirmOpen(true)}
                className="rounded-xl border border-destructive/30 px-4 py-2 text-xs font-medium text-destructive [transition-property:transform,opacity] duration-200 active:scale-[0.96]"
              >
                Delete client
              </button>
            </div>
          </header>

          {data.status?.note ? (
            <p className="mt-6 rounded-2xl border border-foreground/10 bg-white p-5 text-sm text-foreground/70">
              {data.status.note}
            </p>
          ) : null}

          <Section title="Proposals">
            {data.proposals.length === 0 ? (
              <Empty>No proposals yet.</Empty>
            ) : (
              <ul className="divide-y divide-foreground/10">
                {data.proposals.map((p) => (
                  <li key={p.id} className="flex items-center justify-between gap-4 px-5 py-4">
                    <div className="min-w-0">
                      <p className="truncate text-sm text-foreground">{p.title}</p>
                      <p className="font-mono text-xs tabular-nums text-foreground/50">
                        {p.number} · {p.status} · {date(p.created_at)}
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-4">
                      <span className="font-mono text-sm tabular-nums">
                        {money(p.amount_total_cents, p.currency)}
                      </span>
                      <a
                        href={`/p/${p.token}`}
                        target="_blank"
                        rel="noopener"
                        className="text-[11px] uppercase tracking-wider text-foreground/50 underline underline-offset-4 hover:text-foreground"
                      >
                        Open
                      </a>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Section>

          <Section title="Invoices">
            {data.invoices.length === 0 ? (
              <Empty>No invoices issued.</Empty>
            ) : (
              <ul className="divide-y divide-foreground/10">
                {data.invoices.map((i) => (
                  <li key={i.id} className="flex items-center justify-between gap-4 px-5 py-4">
                    <p className="font-mono text-sm tabular-nums">{i.number}</p>
                    <p className="text-xs text-foreground/50">
                      {i.status} · {date(i.issued_at)}
                    </p>
                    <p className="font-mono text-sm tabular-nums">
                      {money(i.gross_cents, i.currency)}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </Section>

          <Section title="Payments">
            {data.orders.length === 0 ? (
              <Empty>No payments recorded.</Empty>
            ) : (
              <ul className="divide-y divide-foreground/10">
                {data.orders.map((o) => (
                  <li key={o.id} className="flex items-center justify-between gap-4 px-5 py-4">
                    <p className="text-sm">{o.status}</p>
                    <p className="text-xs text-foreground/50">{date(o.created_at)}</p>
                    <p className="font-mono text-sm tabular-nums">
                      {money(o.amount_total, o.currency)}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </Section>

          <Section title="Requests">
            {data.leads.length === 0 ? (
              <Empty>No inbound requests from this email.</Empty>
            ) : (
              <ul className="divide-y divide-foreground/10">
                {data.leads.map((l) => (
                  <li key={`${l.source}-${l.id}`} className="px-5 py-4">
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                      {l.source} · {l.admin_status} · {date(l.created_at)}
                    </p>
                    <p className="mt-1 text-sm text-pretty text-foreground/70">{l.summary}</p>
                  </li>
                ))}
              </ul>
            )}
          </Section>

          <ConfirmDialog
            open={confirmOpen}
            onOpenChange={setConfirmOpen}
            title="Delete this client?"
            description="The login and profile are removed permanently. Proposals, invoices and payments stay on record and are detached from the account."
            confirmWord={data.profile.email}
            confirmLabel="Delete client"
            pending={deleteMut.isPending}
            error={deleteMut.error ? (deleteMut.error as Error).message : null}
            onConfirm={() => deleteMut.mutate()}
          />
        </>
      ) : null}
    </main>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-10">
      <h2 className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
        {title}
      </h2>
      <div className="mt-3 overflow-hidden rounded-2xl border border-foreground/10 bg-white">
        {children}
      </div>
    </section>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <p className="px-5 py-6 text-sm text-foreground/50">{children}</p>;
}
